import React, { useEffect, useMemo, useState } from 'react';
import { CheckPicker, Input, InputNumber } from 'rsuite';
import { Button, Modal, renderSelectAllFooter } from '@nsi-advisory-services/components';
import { renderEmployeeSelection } from '../shared/employeeSelection';

// One reimbursement adjustment for several employees at once.
//
// Opened from the group button on the Reimbursement Adjustment row, for the
// case that row is usually filled in for: the same amount to everybody who was
// at something - "$15 lunch for the all-hands". It sets each picked employee's
// adjustment to this amount and reason, the same as typing it into each of
// their cells, so somebody who already had one has it replaced; the dialog says
// so before it happens. An amount of zero clears them.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export default function PayrollBulkReimbursementAdjustmentModal({
  open, employees, onClose, onApply, saving,
}) {
  const [selected, setSelected] = useState([]);
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState('');

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setAmount(null);
    setReason('');
  }, [open]);

  const options = useMemo(
    () => (employees || []).map((employee) => ({ label: employee.name, value: employee.employeeId })),
    [employees]
  );

  const numeric = amount === '' || amount == null ? NaN : Number(amount);
  const valid = Number.isFinite(numeric) && selected.length > 0;
  const clearing = valid && numeric === 0;

  // Who already has an adjustment that this would overwrite.
  const replacing = useMemo(() => {
    const picked = new Set(selected);
    return (employees || []).filter(
      (employee) => picked.has(employee.employeeId) && Number(employee.reimbursementAdjustment || 0) !== 0
    );
  }, [employees, selected]);

  const handleApply = async () => {
    if (!valid) return;
    try {
      await onApply({ employeeIds: selected, amount: numeric, note: reason.trim() || null });
      onClose();
    } catch {
      // The page has said what went wrong; the form stays filled in.
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="sm" overflow={false}>
      <Modal.Header>
        <Modal.Title>Reimbursement adjustment for a group</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="bdc-modal__label">Employees</div>
        <CheckPicker
          data={options}
          countable={false}
          renderValue={renderEmployeeSelection(options)}
          value={selected}
          onChange={(next) => setSelected(next || [])}
          placeholder="Pick who it applies to"
          searchable
          block
          renderExtraFooter={renderSelectAllFooter(options, selected, setSelected)}
          style={{ marginBottom: 16 }}
        />

        <div className="bdc-modal__label">Amount per employee</div>
        <InputNumber
          value={amount}
          onChange={setAmount}
          onFocus={(event) => event.target.select()}
          prefix="$"
          step={1}
          placeholder="15.00"
          style={{ width: 180, marginBottom: 16 }}
        />

        <div className="bdc-modal__label">Reason</div>
        <Input
          as="textarea"
          rows={3}
          value={reason}
          onChange={setReason}
          placeholder="What is this for? e.g. Lunch at the October all-hands"
          disabled={clearing}
        />

        <div className="payroll-correction-modal__hint">
          {clearing
            ? `Clears the reimbursement adjustment for ${selected.length} employee${selected.length === 1 ? '' : 's'}.`
            : valid
              ? `${selected.length} × ${fmtMoney(numeric)} = ${fmtMoney(selected.length * numeric)}, added to reimbursements and Cash Needed.`
              : 'Each employee picked gets this amount on their Reimbursement Adjustment row. A negative amount takes money off; zero clears it.'}
        </div>

        {replacing.length > 0 && !clearing ? (
          <div className="payroll-bulkadj__replacing">
            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{' '}
            Replaces the existing adjustment for{' '}
            {replacing.map((employee) => `${employee.name} (${fmtMoney(employee.reimbursementAdjustment)})`).join(', ')}.
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleApply} loading={saving} disabled={!valid}>
          {clearing
            ? 'Clear'
            : selected.length > 0
              ? `Apply to ${selected.length} employee${selected.length === 1 ? '' : 's'}`
              : 'Apply'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
